import type {
  AuthUser,
  CompleteOnboardingNewOrgInput,
  LoginInput,
  RegisterInput,
} from '@sea-snag/shared';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { authApi, clearTokens, getAccessToken, setAccessToken } from '../lib/api-client';
import { clearOrganization } from '../stores/org-store';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface CompleteOnboardingData {
  token: string;
  name: string;
  organizationName: string;
  teamSize: string;
  password: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  login: (data: LoginInput) => Promise<AuthUser>;
  register: (data: RegisterInput) => Promise<void>;
  completeOnboarding: (
    data: CompleteOnboardingData
  ) => Promise<{ organization: { id: string; name: string; slug: string } }>;
  completeOnboardingNewOrg: (
    data: CompleteOnboardingNewOrgInput
  ) => Promise<{ organization: { id: string; name: string; slug: string } }>;
  refetchUser: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAccessToken();
      if (!token) {
        setStatus('unauthenticated');
        return;
      }

      try {
        const currentUser = await authApi.me();
        setUser(currentUser);
        setStatus('authenticated');
      } catch {
        clearTokens();
        setStatus('unauthenticated');
      }
    };

    void checkAuth();
  }, []);

  const login = useCallback(async (data: LoginInput): Promise<AuthUser> => {
    const response = await authApi.login(data);
    setAccessToken(response.accessToken);
    const currentUser = await authApi.me();
    setUser(currentUser);
    setStatus('authenticated');
    return currentUser;
  }, []);

  const register = useCallback(async (data: RegisterInput) => {
    const response = await authApi.register(data);
    setAccessToken(response.accessToken);
    const currentUser = await authApi.me();
    setUser(currentUser);
    setStatus('authenticated');
  }, []);

  const completeOnboarding = useCallback(async (data: CompleteOnboardingData) => {
    const response = await authApi.completeOnboarding(data);
    setAccessToken(response.accessToken);
    const currentUser = await authApi.me();
    setUser(currentUser);
    setStatus('authenticated');
    return { organization: response.organization };
  }, []);

  const completeOnboardingNewOrg = useCallback(async (data: CompleteOnboardingNewOrgInput) => {
    const response = await authApi.completeOnboardingNewOrg(data);
    setAccessToken(response.accessToken);
    const currentUser = await authApi.me();
    setUser(currentUser);
    setStatus('authenticated');
    return { organization: response.organization };
  }, []);

  const refetchUser = useCallback(async () => {
    try {
      const currentUser = await authApi.me();
      setUser(currentUser);
      setStatus('authenticated');
    } catch {
      // ignore — if auth has become invalid, an existing 401 handler will fire
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors - we'll clear local state anyway
    }
    clearTokens();
    clearOrganization();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Multi-tab sync: when another tab logs out, sync this tab
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'accessToken' && !e.newValue) {
        setUser(null);
        setStatus('unauthenticated');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      login,
      register,
      completeOnboarding,
      completeOnboardingNewOrg,
      refetchUser,
      logout,
    }),
    [
      user,
      status,
      login,
      register,
      completeOnboarding,
      completeOnboardingNewOrg,
      refetchUser,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
